import React, { useState } from "react";
import {
  Modal,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import { supabase } from "../supabaseClient";

export default function AuthModal({ open, onClose }) {
  const [authMode, setAuthMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAuth = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (authMode === "login") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        // Validate username
        if (!username || username.length < 3) {
          throw new Error("Username must be at least 3 characters.");
        }
        
        // Check if username is unique
        const { data: existing } = await supabase
          .from("profiles")
          .select("id")
          .eq("username", username)
          .single();
        
        if (existing) {
          throw new Error("Username is already taken.");
        }
        
        // Sign up user
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        
        if (data?.user) {
          const userId = data.user.id;
          
          // Insert into profiles
          const { error: profileError } = await supabase.from("profiles").insert([
            { id: userId, username }
          ]);
          if (profileError) throw new Error("Profile creation error: " + profileError.message);
          
          // Insert into users table for role management
          const { error: usersError } = await supabase.from("users").insert([
            { id: userId, email, role: "user" }
          ]);
          if (usersError) throw new Error("User role creation error: " + usersError.message);
          
          alert("Check your email for verification!");
        }
      }
      
      // Clear form
      setEmail("");
      setPassword("");
      setUsername("");
      onClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setAuthMode(authMode === "login" ? "signup" : "login");
    setError("");
    setEmail("");
    setPassword("");
    setUsername("");
  };

  const handleClose = () => {
    setError("");
    setEmail("");
    setPassword("");
    setUsername("");
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          outline: "none",
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={authMode}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <Paper
              sx={{
                minWidth: { xs: 340, sm: 420 },
                maxWidth: 480,
                p: 0,
                borderRadius: 4,
                outline: "none",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
                background: "linear-gradient(135deg, #232323 0%, #2e2e2e 100%)",
                color: "#fff",
                border: "2px solid #3e3e3e",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                  px: 4,
                  py: 3,
                  position: "relative",
                  borderBottom: "2px solid #f49330",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    textAlign: "center",
                    background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: { xs: 28, sm: 32 },
                  }}
                >
                  {authMode === "login" ? "WELCOME BACK" : "JOIN THE BATTLE"}
                </Typography>
                
                <IconButton
                  onClick={handleClose}
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: 16,
                    color: "#f49330",
                    "&:hover": {
                      color: "#fff176",
                      background: "rgba(244, 147, 48, 0.1)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Form Content */}
              <Box sx={{ p: 4 }}>
                <form onSubmit={handleAuth}>
                  <Stack spacing={3}>
                    {/* Email Field */}
                    <Box sx={{ position: "relative" }}>
                      <EmailIcon
                        sx={{
                          position: "absolute",
                          left: 16,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#f49330",
                          zIndex: 1,
                        }}
                      />
                      <TextField
                        label="Email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        fullWidth
                        sx={{
                          "& .MuiInputBase-root": {
                            background: "linear-gradient(135deg, #181818 0%, #232323 100%) !important",
                            borderRadius: 3,
                            paddingLeft: "48px",
                          },
                          "& .MuiInputBase-input": {
                            color: "#fff !important",
                            fontSize: 16,
                            paddingLeft: 0,
                            WebkitTextFillColor: "#fff !important",
                            "&:-webkit-autofill": {
                              WebkitBoxShadow: "0 0 0 1000px #232323 inset !important",
                              WebkitTextFillColor: "#fff !important",
                              borderRadius: "12px !important",
                              transition: "background-color 5000s ease-in-out 0s !important",
                            },
                            "&:-webkit-autofill:hover": {
                              WebkitBoxShadow: "0 0 0 1000px #232323 inset !important",
                              WebkitTextFillColor: "#fff !important",
                            },
                            "&:-webkit-autofill:focus": {
                              WebkitBoxShadow: "0 0 0 1000px #232323 inset !important",
                              WebkitTextFillColor: "#fff !important",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "#999",
                            fontSize: 16,
                            left: "48px",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#f49330",
                            left: "48px",
                          },
                          "& .MuiInputLabel-shrink": {
                            left: 0,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#555",
                            borderWidth: 2,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#f49330",
                          },
                          "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#f49330",
                          },
                        }}
                      />
                    </Box>

                    {/* Password Field */}
                    <Box sx={{ position: "relative" }}>
                      <LockIcon
                        sx={{
                          position: "absolute",
                          left: 16,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#f49330",
                          zIndex: 1,
                        }}
                      />
                      <TextField
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        fullWidth
                        sx={{
                          "& .MuiInputBase-root": {
                            background: "linear-gradient(135deg, #181818 0%, #232323 100%) !important",
                            borderRadius: 3,
                            paddingLeft: "48px",
                          },
                          "& .MuiInputBase-input": {
                            color: "#fff !important",
                            fontSize: 16,
                            paddingLeft: 0,
                            WebkitTextFillColor: "#fff !important",
                            "&:-webkit-autofill": {
                              WebkitBoxShadow: "0 0 0 1000px #232323 inset !important",
                              WebkitTextFillColor: "#fff !important",
                              borderRadius: "12px !important",
                              transition: "background-color 5000s ease-in-out 0s !important",
                            },
                            "&:-webkit-autofill:hover": {
                              WebkitBoxShadow: "0 0 0 1000px #232323 inset !important",
                              WebkitTextFillColor: "#fff !important",
                            },
                            "&:-webkit-autofill:focus": {
                              WebkitBoxShadow: "0 0 0 1000px #232323 inset !important",
                              WebkitTextFillColor: "#fff !important",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "#999",
                            fontSize: 16,
                            left: "48px",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#f49330",
                            left: "48px",
                          },
                          "& .MuiInputLabel-shrink": {
                            left: 0,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#555",
                            borderWidth: 2,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#f49330",
                          },
                          "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#f49330",
                          },
                        }}
                      />
                    </Box>

                    {/* Username Field (Signup only) */}
                    <AnimatePresence>
                      {authMode === "signup" && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Box sx={{ position: "relative" }}>
                            <PersonIcon
                              sx={{
                                position: "absolute",
                                left: 16,
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "#f49330",
                                zIndex: 1,
                              }}
                            />
                            <TextField
                              label="Username"
                              type="text"
                              value={username}
                              onChange={(e) => setUsername(e.target.value)}
                              required
                              fullWidth
                              sx={{
                                "& .MuiInputBase-root": {
                                  background: "linear-gradient(135deg, #181818 0%, #232323 100%)",
                                  borderRadius: 3,
                                  paddingLeft: "48px",
                                },
                                "& .MuiInputBase-input": {
                                  color: "#fff",
                                  fontSize: 16,
                                  paddingLeft: 0,
                                },
                                "& .MuiInputLabel-root": {
                                  color: "#999",
                                  fontSize: 16,
                                  left: "48px",
                                },
                                "& .MuiInputLabel-root.Mui-focused": {
                                  color: "#f49330",
                                  left: "48px",
                                },
                                "& .MuiInputLabel-shrink": {
                                  left: 0,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#555",
                                  borderWidth: 2,
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#f49330",
                                },
                                "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#f49330",
                                },
                              }}
                            />
                          </Box>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Error Message */}
                    <AnimatePresence>
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Box
                            sx={{
                              background: "rgba(211, 47, 47, 0.1)",
                              border: "1px solid rgba(211, 47, 47, 0.3)",
                              borderRadius: 2,
                              p: 2,
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#ff5252",
                                textAlign: "center",
                                fontSize: 14,
                                fontWeight: 600,
                              }}
                            >
                              {error}
                            </Typography>
                          </Box>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={loading}
                      sx={{
                        py: 2,
                        fontSize: 18,
                        fontWeight: 700,
                        borderRadius: 3,
                        background: loading 
                          ? "linear-gradient(135deg, #444 0%, #666 100%)"
                          : "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                        color: "#181818",
                        boxShadow: loading 
                          ? "none"
                          : "0 4px 20px rgba(244, 147, 48, 0.4)",
                        "&:hover": {
                          background: loading
                            ? "linear-gradient(135deg, #444 0%, #666 100%)"
                            : "linear-gradient(45deg, #e8841f 30%, #f9e71e 90%)",
                          boxShadow: loading
                            ? "none"
                            : "0 6px 25px rgba(244, 147, 48, 0.6)",
                          transform: loading ? "none" : "translateY(-2px)",
                        },
                        "&:disabled": {
                          opacity: 0.7,
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {loading 
                        ? (authMode === "login" ? "LOGGING IN..." : "CREATING ACCOUNT...")
                        : (authMode === "login" ? "LOGIN" : "CREATE ACCOUNT")
                      }
                    </Button>
                  </Stack>
                </form>

                {/* Divider */}
                <Divider sx={{ my: 4, borderColor: "#444" }} />

                {/* Switch Mode Button */}
                <Button
                  onClick={switchMode}
                  fullWidth
                  sx={{
                    py: 2,
                    fontSize: 16,
                    fontWeight: 600,
                    borderRadius: 3,
                    background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                    color: "#f49330",
                    border: "2px solid #555",
                    "&:hover": {
                      background: "rgba(244, 147, 48, 0.1)",
                      borderColor: "#f49330",
                      color: "#fff176",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {authMode === "login"
                    ? "Don't have an account? Sign Up"
                    : "Already have an account? Login"}
                </Button>
              </Box>
            </Paper>
          </motion.div>
        </AnimatePresence>
      </Box>
    </Modal>
  );
}